/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  faTrash,
  faPlay,
  faSpinner,
  faCopy,
  faMagnifyingGlass,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MonacoEditor, { OnMount } from '@monaco-editor/react';
import { EditorEvents, Editor as TipTapEditor } from '@tiptap/core';
import { Node as PMNode } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { NodeViewWrapper } from '@tiptap/react';
import { useEffect, useState, useCallback, memo, useContext } from 'react';

import { CodeExecutionResult } from './CodeExecutionResult';
import {
  useRunCodeExecution,
  isSupportedCodeRunLanguage,
} from '../../../../hooks/useRunCodeExecution';

import { SelectMenu, Tooltip } from '@scale/scaleui';
import useStateDelayed from '../../../../hooks/useStateDelayed';
import { ChatTaskContext } from '../../../../contexts/ChatTaskContext';
import { useWorkerCommentsStore } from '../../../../hooks/useWorkerCommentsStore';

const DEFAULT_MONACO_HEIGHT = 400;
const CODE_OPTIMIZATION_THRESHOLD = 10000;

const MONACO_TO_MARKDOWN = {
  javascript: 'js', // javascript, js, node
  js: 'js',
  typescript: 'ts', // ts, typescript
  ts: 'ts',
  python: 'python', // py, python
  java: 'java',
  shell: 'sh',
  c: 'c',
  cpp: 'cpp',
  jsx: 'jsx',
  json: 'json',
  plaintext: 'text',
  html: 'html',
  swift: 'swift',
  go: 'go',
  csharp: 'csharp',
};

type SupportedMonacoLanguages = keyof typeof MONACO_TO_MARKDOWN;

const LANGUAGE_TITLES: Record<SupportedMonacoLanguages, string> = {
  javascript: 'JavaScript',
  js: 'JavaScript',
  typescript: 'TypeScript',
  ts: 'TypeScript',
  python: 'Python',
  java: 'Java',
  shell: 'BashScript',
  c: 'C',
  cpp: 'C++',
  jsx: 'JSX',
  json: 'JSON',
  plaintext: 'Plaintext',
  html: 'HTML',
  swift: 'Swift',
  go: 'Go',
  csharp: 'C#',
};

type IStandaloneCodeEditor = Parameters<OnMount>[0];

const MARKDOWN_TO_MONACO = Object.entries(MONACO_TO_MARKDOWN).reduce<{
  [key: string]: string;
}>((acc, [monaco, markdown]) => {
  acc[markdown] = monaco;
  return acc;
}, {});

const ExecutableCodeBlockNodeView = memo(
  ({
    editor: proseMirrorEditor,
    node: proseMirrorNode,
    getPos,
    updateAttributes,
  }: {
    selected: boolean;
    editor: TipTapEditor;
    node: PMNode;
    getPos: () => number;
    updateAttributes: (attrs: Record<string, any>) => void;
  }) => {
    const { enableFactChecker } = useContext(ChatTaskContext);
    const monacoLanguage: SupportedMonacoLanguages = (MARKDOWN_TO_MONACO[
      proseMirrorNode?.attrs?.language
    ] || MARKDOWN_TO_MONACO.text) as SupportedMonacoLanguages;

    const [readOnly, setReadOnly] = useState(!proseMirrorEditor.isEditable);
    const [selection, setSelection] = useStateDelayed<string>('', 200);

    const setCommentSelection = useWorkerCommentsStore(state => state.setSelection);

    const code: string = (proseMirrorNode as any)?.content?.content?.[0]?.text ?? '';
    const disableCopy = proseMirrorEditor.extensionStorage.noCopyPaste?.disableCopy;
    const disablePaste = proseMirrorEditor.extensionStorage.noCopyPaste?.disablePaste;

    const [monacoDiffEditor, setMonacoDiffEditor] = useState<IStandaloneCodeEditor | null>(null);

    const [monacoHeight, setMonacoHeight] = useState(DEFAULT_MONACO_HEIGHT);

    useEffect(() => {
      const handleEditableChange = ({ editor }: EditorEvents['update']) => {
        setReadOnly(!editor.isEditable);
      };
      proseMirrorEditor.on('update', handleEditableChange);
      return () => {
        proseMirrorEditor.off('update', handleEditableChange);
      };
    }, [proseMirrorEditor]);

    const handleSetBlockSelection = useCallback(() => {
      const { state, dispatch } = proseMirrorEditor.view;
      const { tr } = state;
      const docPos = getPos();

      const transaction = tr.setSelection(TextSelection.near(tr.doc.resolve(Math.max(0, docPos))));

      dispatch(transaction);
    }, [getPos, proseMirrorEditor]);

    const handleDeleteBlock = useCallback(() => {
      if (!proseMirrorEditor.isEditable) return;
      const { state, dispatch } = proseMirrorEditor.view;
      const { tr } = state;
      const docPos = getPos();
      tr.delete(docPos, docPos + proseMirrorNode.nodeSize);
      proseMirrorEditor.commands.focus(docPos > 0 ? docPos - 1 : docPos);
      dispatch(tr);
    }, [
      getPos,
      proseMirrorEditor.isEditable,
      proseMirrorEditor.commands,
      proseMirrorEditor.view,
      proseMirrorNode.nodeSize,
    ]);

    const handleEditorDidMount = useCallback<OnMount>(monacoDiffEditor => {
      setMonacoDiffEditor(monacoDiffEditor);
      setMonacoHeight(monacoDiffEditor.getContentHeight());
    }, []);

    useEffect(() => {
      const updateEditorHeight = () => {
        if (!monacoDiffEditor) return;
        setMonacoHeight(monacoDiffEditor.getContentHeight());
      };

      const onDidChangeCursorSelection = monacoDiffEditor?.onDidChangeCursorSelection(() => {
        const selectedText =
          monacoDiffEditor.getModel()?.getValueInRange(
            monacoDiffEditor.getSelection() || {
              startLineNumber: 0,
              startColumn: 0,
              endLineNumber: 0,
              endColumn: 0,
            },
          ) || '';
        setSelection(selectedText);
        // Add your custom logic here
      });

      // on first initialization
      const didChangeModelLanguageConfigurationDisposable =
        monacoDiffEditor?.onDidChangeModelLanguageConfiguration(updateEditorHeight);

      // on every initialization
      const didLayoutChangeDisposable = monacoDiffEditor?.onDidLayoutChange(updateEditorHeight);

      const onKeyDownDisposable = monacoDiffEditor?.onKeyDown((event: any) => {
        const { state } = proseMirrorEditor.view;
        const { doc } = state;
        const docPos = getPos();

        if (event.code === 'Backspace') {
          const node = doc.nodeAt(docPos);

          if (!node?.textContent) {
            handleDeleteBlock();
          }
        }

        if (event.code === 'ArrowDown') {
          const position = monacoDiffEditor.getPosition();
          const model = monacoDiffEditor.getModel();

          if (model === null || position === null) return;

          const lastLineNumber = model.getLineCount();

          if (position.lineNumber === lastLineNumber) {
            proseMirrorEditor.commands.exitCode();
            proseMirrorEditor.commands.focus();
          }
        }

        if (event.code === 'ArrowUp') {
          const position = monacoDiffEditor.getPosition();

          if (position?.lineNumber === 1) {
            proseMirrorEditor.commands.insertParagraphBefore();
            proseMirrorEditor.commands.focus();
          }
        }

        // Disable Copy
        if (
          disableCopy &&
          (event.ctrlKey || event.metaKey) &&
          // 33 is the code for KeyCode.KeyC for monaco-editor, we shouldn't import it
          // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
          event.keyCode === 33
        ) {
          event.preventDefault();
        }

        // Disable Paste
        if (
          disablePaste &&
          (event.ctrlKey || event.metaKey) &&
          // 52 is the code for KeyCode.KeyV for monaco-editor, we shouldn't import it
          // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
          event.keyCode === 52
        ) {
          event.preventDefault();
        }
      });

      const didFocusEditorWidgetDisposable = monacoDiffEditor?.onDidFocusEditorWidget(() => {
        handleSetBlockSelection();
        monacoDiffEditor.focus();
      });

      return () => {
        onDidChangeCursorSelection?.dispose();
        didChangeModelLanguageConfigurationDisposable?.dispose();
        didLayoutChangeDisposable?.dispose();
        didFocusEditorWidgetDisposable?.dispose();
        onKeyDownDisposable?.dispose();
      };
    }, [
      monacoDiffEditor,
      setSelection,
      getPos,
      handleSetBlockSelection,
      proseMirrorEditor,
      handleDeleteBlock,
    ]);

    useEffect(() => {
      if (!selection) {
        proseMirrorEditor.commands.setCopilotOpen(false);
      }
    }, [proseMirrorEditor, selection, setCommentSelection, getPos]);

    const handleCopilot = useCallback(() => {
      if (!proseMirrorEditor) return;
      const docPos = getPos();
      proseMirrorEditor.commands.setCopilotSelection({
        empty: selection === '',
        text: selection,
        from: Math.max(0, docPos),
        to: 0,
      });
      proseMirrorEditor.commands.setCopilotOpen(true);
    }, [proseMirrorEditor, selection, getPos]);

    const handleCodeChange = useCallback(
      (newCode?: string) => {
        const { state, dispatch } = proseMirrorEditor.view;
        const { tr } = state;
        const nodeType = state.schema.nodes.executableCodeBlock;
        const docPos = getPos();
        let transaction;
        if (newCode === proseMirrorNode.content.textBetween(0, proseMirrorNode.content.size, ''))
          return;

        if (newCode) {
          const newMonacoCodeBlock = nodeType.create(
            { ...proseMirrorNode.attrs },
            state.schema.text(newCode),
          );

          transaction = tr.replaceWith(
            docPos,
            docPos + proseMirrorNode.nodeSize,
            newMonacoCodeBlock,
          );
        } else {
          // Create a new empty code block node
          const emptyNewMonacoCodeBlock = nodeType.create({
            ...proseMirrorNode.attrs,
          });

          // Replace the current node with the new empty code block
          transaction = tr.replaceWith(
            docPos,
            docPos + proseMirrorNode.nodeSize,
            emptyNewMonacoCodeBlock,
          );
        }

        transaction.setSelection(TextSelection.near(tr.doc.resolve(Math.max(0, docPos))));

        dispatch(transaction);
      },
      [getPos, proseMirrorEditor, proseMirrorNode],
    );

    const [{ loading: isRunningCodeExecution, value: codeExecutionResponse }, runCodeExecution] =
      useRunCodeExecution({ language: monacoLanguage as string, code });

    const optimizedOptions =
      code.length > CODE_OPTIMIZATION_THRESHOLD
        ? {
            folding: false,
            renderLineHighlight: 'none',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            wordWrap: 'off' as const,
            occurrencesHighlight: false,
            selectionHighlight: false,
            renderWhitespace: 'none',
            suggestOnTriggerCharacters: false,
            tabCompletion: 'off',
            contextmenu: false,
            quickSuggestions: false,
            renderIndentGuides: false,
          }
        : {};

    return (
      <NodeViewWrapper
        as="div"
        contentEditable={false}
        dir="auto"
        className="react-monaco-code-block bg-neutral-800 not-wysiwyg font-mono text-neutral-0 border-neutral-700 border rounded overflow-hidden"
      >
        <div className="flex items-center py-2 px-4 gap-5 border-neutral-700 border-b rounded-t">
          <div>
            <SelectMenu<SupportedMonacoLanguages>
              isInPortal
              menuDirection="bottom"
              optionsClassName="scale-theme border border-neutral-400"
              disabled={readOnly}
              selectedOptionId={monacoLanguage as SupportedMonacoLanguages}
              options={Object.keys(MONACO_TO_MARKDOWN).map(id => ({
                id: id as SupportedMonacoLanguages,
                name: LANGUAGE_TITLES[id as SupportedMonacoLanguages],
              }))}
              setSelected={value => {
                updateAttributes({
                  language: MONACO_TO_MARKDOWN[value],
                });
              }}
            />
          </div>

          {isSupportedCodeRunLanguage(monacoLanguage) && (
            <Tooltip
              text={isRunningCodeExecution ? 'Running code execution...' : 'Run code execution'}
              target={
                <div
                  className="flex items-center"
                  onClick={!isRunningCodeExecution ? runCodeExecution : undefined}
                >
                  <FontAwesomeIcon
                    className="text-muted-contrast cursor-pointer h-4 w-4"
                    spin={isRunningCodeExecution}
                    icon={isRunningCodeExecution ? faSpinner : faPlay}
                  />
                </div>
              }
            />
          )}

          {!disableCopy && (
            <Tooltip
              text="Copy code to clipboard"
              target={
                <FontAwesomeIcon
                  className="text-muted-contrast cursor-pointer"
                  icon={faCopy}
                  onClick={event => {
                    event.preventDefault();
                    navigator.clipboard.writeText(code);
                  }}
                />
              }
            />
          )}

          {proseMirrorEditor.isEditable && (
            <Tooltip
              text="Delete code block"
              target={
                <div className="flex items-center cursor-pointer">
                  <FontAwesomeIcon
                    onClick={handleDeleteBlock}
                    icon={faTrash}
                    className="text-muted-contrast cursor-pointer h-4 w-4"
                  />
                </div>
              }
            ></Tooltip>
          )}
          {selection && enableFactChecker && (
            <Tooltip
              text="Copilot"
              target={
                <div className="flex items-center cursor-pointer">
                  <FontAwesomeIcon
                    onClick={handleCopilot}
                    icon={faMagnifyingGlass}
                    className="text-muted-contrast cursor-pointer h-4 w-4"
                  />
                </div>
              }
            ></Tooltip>
          )}

          {code.length > CODE_OPTIMIZATION_THRESHOLD && (
            <Tooltip
              text="Some features have been disabled due to the long code block"
              target={
                <div className="flex items-center">
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="text-muted-contrast cursor-pointer h-4 w-4"
                  />
                </div>
              }
            />
          )}
        </div>

        <div className="pr-4">
          <MonacoEditor
            theme={'vs-dark'}
            height={monacoHeight}
            wrapperProps={{ className: 'reset-all-css rounded-b' }}
            value={code}
            language={code.length > CODE_OPTIMIZATION_THRESHOLD ? 'plaintext' : monacoLanguage}
            options={
              {
                lineNumbers: 'on',
                roundedSelection: false,
                scrollBeyondLastLine: false,
                minimap: { enabled: false },
                padding: { top: 10, bottom: 10 },
                lineDecorationsWidth: 5,
                lineNumbersMinChars: 4,
                unicodeHighlight: { ambiguousCharacters: false },
                wordWrap: 'on' as const,
                overviewRulerBorder: false,
                domReadOnly: readOnly,
                readOnly,
                ...optimizedOptions,
              } as any // Disable type checking for now
            }
            onMount={handleEditorDidMount}
            onChange={code => {
              if (!monacoDiffEditor) return;
              setMonacoHeight(monacoDiffEditor.getContentHeight());
              handleCodeChange?.(code);
            }}
          />

          {(isRunningCodeExecution || codeExecutionResponse) && (
            <CodeExecutionResult
              isRunningCodeExecution={isRunningCodeExecution}
              codeExecutionResponse={codeExecutionResponse}
            />
          )}
        </div>
      </NodeViewWrapper>
    );
  },
);

export { ExecutableCodeBlockNodeView };
